import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner, Accordion, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle, faImage } from '@fortawesome/free-solid-svg-icons';
import { FullEquipment } from '../interfaces';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';
import MessageModal from './MessageModal';
import DefectImageInput from './DefectImageInput';
import { buildApiUrl } from '../config/api';

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('קריאת הקובץ נכשלה'));
    r.readAsDataURL(file);
  });
}

// הרחבה של FullEquipment עם שדות לדיווח תקלות
interface EquipmentWithReportData extends FullEquipment {
  reported_defective_quantity?: number;
  reported_defect_description?: string;
  reported_priority?: string;
  defect_instances?: DefectInstance[]; // מערך של דיווחי תקלות נפרדים
}

interface DefectInstance {
  id: string; // מזהה ייחודי לדיווח זה
  quantity: number;
  description: string;
  priority: string;
  equipment_id: number; // מזהה הציוד
  equipment_name: string; // שם הציוד
  category_name: string; // שם הקטגוריה
  subcategory_name: string; // שם תת הקטגוריה
  imageFile?: File | null;
}

interface DefectReportGeneralModalProps {
  show: boolean;
  onHide: () => void;
  onDefectReport: (defectData: any) => void;
}

// פונקציה ליצירת רשימת דיווחי תקלות נפרדים
const createDefectInstancesList = (equipment: EquipmentWithReportData[]): DefectInstance[] => {
  const defectInstances: DefectInstance[] = [];
  
  equipment.forEach(item => {
    // דיווח בסיסי (אם קיים)
    if (item.reported_defective_quantity && item.reported_defective_quantity > 0) {
      defectInstances.push({
        id: `basic_${item.id}_${Date.now()}`,
        quantity: item.reported_defective_quantity,
        description: item.reported_defect_description || '',
        priority: item.reported_priority || 'medium',
        equipment_id: item.id!,
        equipment_name: item.name,
        category_name: item.category_name || 'לא מסווג',
        subcategory_name: item.subcategory_name || 'לא מסווג'
      });
    }
    
    // דיווחים נפרדים (אם קיימים)
    if (item.defect_instances && item.defect_instances.length > 0) {
      defectInstances.push(...item.defect_instances);
    }
  });
  
  return defectInstances;
};

// פונקציה לקבץ דיווחי תקלות לפי קטגוריות ותתי קטגוריות
const groupDefectInstancesByCategory = (defectInstances: DefectInstance[]) => {
  const grouped: { [category: string]: { [subcategory: string]: DefectInstance[] } } = {};
  defectInstances.forEach(instance => {
    const category = instance.category_name || 'לא מסווג';
    const subcategory = instance.subcategory_name || 'לא מסווג';
    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
    grouped[category][subcategory].push(instance);
  });
  return grouped;
};

const DefectReportGeneralModal: React.FC<DefectReportGeneralModalProps> = ({ 
  show, 
  onHide, 
  onDefectReport 
}) => {
  const [equipmentList, setEquipmentList] = useState<EquipmentWithReportData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('אישור פעולה');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalTitle, setMessageModalTitle] = useState('');
  const [messageModalContent, setMessageModalContent] = useState('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  const [closeMainModalAfterMessage, setCloseMainModalAfterMessage] = useState(false);

  const priorityOptions = [
    { value: 'low', label: 'נמוך', color: 'text-green-600' },
    { value: 'medium', label: 'בינוני', color: 'text-yellow-600' },
    { value: 'high', label: 'גבוה', color: 'text-orange-600' },
    { value: 'critical', label: 'קריטי', color: 'text-red-600' }
  ];

  useEffect(() => {
    if (show) {
      fetchAllEquipment();
    }
  }, [show]);

  const fetchAllEquipment = async () => {
    setLoading(true);
    try {
      const response = await axios.get(buildApiUrl('inventory/equipment/'));
      setEquipmentList(response.data as EquipmentWithReportData[]);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (equipmentId: number, quantity: number) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.id === equipmentId 
          ? { ...item, reported_defective_quantity: quantity }
          : item
      )
    );
  };

  const handleDescriptionChange = (equipmentId: number, description: string) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.id === equipmentId 
          ? { ...item, reported_defect_description: description }
          : item
      )
    );
  };

  const handlePriorityChange = (equipmentId: number, priority: string) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.id === equipmentId 
          ? { ...item, reported_priority: priority }
          : item
      )
    );
  };

  const actuallyAddDefectInstance = (equipmentId: number) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.id === equipmentId 
          ? { 
              ...item, 
              defect_instances: [
                ...(item.defect_instances || []),
                {
                  id: `defect_${Date.now()}_${Math.random()}`,
                  quantity: 1,
                  description: '',
                  priority: 'medium',
                  equipment_id: item.id!,
                  equipment_name: item.name,
                  category_name: item.category_name || 'לא מסווג',
                  subcategory_name: item.subcategory_name || 'לא מסווג',
                  imageFile: undefined
                }
              ]
            }
          : item
      )
    );
  };

  const addDefectInstance = (equipmentId: number) => {
    // בדיקה האם כבר קיימים דיווחים עבור פריט זה בדיווח הנוכחי
    const currentItem = equipmentList.find(i => i.id === equipmentId);
    const existingCount = (currentItem?.reported_defective_quantity && currentItem.reported_defective_quantity > 0 ? 1 : 0) + (currentItem?.defect_instances?.length || 0);

    if (existingCount > 0) {
      setConfirmModalTitle('הוספת דיווח נוסף');
      setConfirmModalMessage('כבר נבחר פריט מסוג זה בדיווח הזה. האם להוסיף עוד?');
      setConfirmAction(() => () => actuallyAddDefectInstance(equipmentId));
      setShowConfirmModal(true);
      return;
    }

    actuallyAddDefectInstance(equipmentId);
  };

  const removeDefectInstance = (equipmentId: number, instanceId: string) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.id === equipmentId 
          ? { 
              ...item, 
              defect_instances: (item.defect_instances || []).filter(inst => inst.id !== instanceId)
            }
          : item
      )
    );
  };

  const updateDefectInstance = (equipmentId: number, instanceId: string, field: keyof DefectInstance, value: any) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.id === equipmentId 
          ? { 
              ...item, 
              defect_instances: (item.defect_instances || []).map(inst => 
                inst.id === instanceId 
                  ? { ...inst, [field]: value }
                  : inst
              )
            }
          : item
      )
    );
  };

  // יצירת רשימת דיווחי תקלות נפרדים וקיבוץ לפי קטגוריות
  const defectInstances = useMemo(() => createDefectInstancesList(equipmentList), [equipmentList]);
  const groupedDefectInstances = useMemo(() => groupDefectInstancesByCategory(defectInstances), [defectInstances]);

  const handleSubmit = async () => {
    const defectiveEquipment = equipmentList.filter(item => 
      (item.reported_defective_quantity && item.reported_defective_quantity > 0) ||
      (item.defect_instances && item.defect_instances.length > 0)
    );

    if (defectiveEquipment.length === 0) {
      setMessageModalTitle('שים לב');
      setMessageModalContent('לא נבחרו פריטים תקולים לדיווח');
      setMessageModalVariant('warning');
      setCloseMainModalAfterMessage(false);
      setShowMessageModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const defective_equipment: {
        equipment_id: number;
        quantity_defective: number;
        defect_description: string;
        priority: string;
        image_base64?: string;
      }[] = [];

      for (const item of defectiveEquipment) {
        if (item.reported_defective_quantity && item.reported_defective_quantity > 0) {
          defective_equipment.push({
            equipment_id: item.id!,
            quantity_defective: item.reported_defective_quantity,
            defect_description: item.reported_defect_description || '',
            priority: item.reported_priority || 'medium'
          });
        }
        if (item.defect_instances && item.defect_instances.length > 0) {
          for (const instance of item.defect_instances) {
            if (instance.quantity > 0) {
              const row: (typeof defective_equipment)[0] = {
                equipment_id: item.id!,
                quantity_defective: instance.quantity,
                defect_description: instance.description,
                priority: instance.priority
              };
              if (instance.imageFile) {
                row.image_base64 = await readFileAsDataUrl(instance.imageFile);
              }
              defective_equipment.push(row);
            }
          }
        }
      }

      const defectData = { defective_equipment };

      await axios.post(buildApiUrl('inventory/equipment/report_defects/'), defectData);
      await onDefectReport(defectData);
      setMessageModalTitle('הצלחה');
      setMessageModalContent('דיווח התקלות נשלח בהצלחה.');
      setMessageModalVariant('success');
      setCloseMainModalAfterMessage(true);
      setShowMessageModal(true);
    } catch (error: unknown) {
      console.error('Error submitting defect report:', error);
      const msg =
        axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object' && 'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'שגיאה בשליחת דיווח התקלות';
      setMessageModalTitle('שגיאה');
      setMessageModalContent(msg);
      setMessageModalVariant('danger');
      setCloseMainModalAfterMessage(false);
      setShowMessageModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessageModalHide = () => {
    setShowMessageModal(false);
    if (closeMainModalAfterMessage) {
      setCloseMainModalAfterMessage(false);
      onHide();
    }
  };

  if (!show) return null;

  return (
    <>
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
          דיווח תקלות - כל הציוד במחסן
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">טוען רשימת ציוד...</p>
          </div>
        ) : (
          <>
            <Alert variant="info" className="mb-3">
              בחר את הפריטים התקולים ודווח על התקלות. השאר שדות ריקים עבור פריטים תקינים.
            </Alert>

            {/* רשימת ציוד להוספת דיווחי תקלות חדשים */}
            <div className="mb-4">
              <h5>הוסף דיווחי תקלות חדשים:</h5>
              <Accordion defaultActiveKey="0" className="accordion-flush">
                {Object.entries(equipmentList.reduce((acc, item) => {
                  const category = item.category_name || 'לא מסווג';
                  const subcategory = item.subcategory_name || 'לא מסווג';
                  if (!acc[category]) acc[category] = {};
                  if (!acc[category][subcategory]) acc[category][subcategory] = [];
                  acc[category][subcategory].push(item);
                  return acc;
                }, {} as { [category: string]: { [subcategory: string]: EquipmentWithReportData[] } })).map(([category, subcategories], categoryIndex) => (
                  <Accordion.Item eventKey={`equipment_${categoryIndex}`} key={`equipment_${category}`}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <span>
                          <strong>{category}</strong>
                          <Badge bg="primary" className="ms-2">
                            {Object.values(subcategories).flat().length} פריטים
                          </Badge>
                        </span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Accordion defaultActiveKey="0" className="accordion-flush">
                        {Object.entries(subcategories).map(([subcategory, items], subcategoryIndex) => (
                          <Accordion.Item eventKey={`equipment_${categoryIndex}-${subcategoryIndex}`} key={`equipment_${category}-${subcategory}`}>
                            <Accordion.Header>
                              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                <span>
                                  {subcategory}
                                  <Badge bg="info" className="ms-2">
                                    {items.length} פריטים
                                  </Badge>
                                </span>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              {items.map((item) => (
                                <div key={item.id} className="border rounded p-3 mb-3">
                                  <div className="row">
                                    {/* מידע על הפריט */}
                                    <div className="col-md-6">
                                      <h6 className="fw-bold">{item.name}</h6>
                                      <p className="text-muted mb-1 small">
                                        {item.category_name} - {item.subcategory_name}
                                      </p>
                                      <small className="text-muted">
                                        כמות במלאי: {item.quantity} | 
                                        כמות זמינה: {item.available_quantity || item.quantity} | 
                                        כמות תקולה נוכחית: {item.defective_quantity || 0}
                                      </small>
                                    </div>

                                    {/* כפתור הוספת דיווח */}
                                    <div className="col-md-6 d-flex align-items-center justify-content-end">
                                      {((item.reported_defective_quantity && item.reported_defective_quantity > 0 ? 1 : 0) + (item.defect_instances?.length || 0)) > 0 && (
                                        <Badge bg="secondary" className="me-2">
                                          נבחר 
                                          {((item.reported_defective_quantity && item.reported_defective_quantity > 0 ? 1 : 0) + (item.defect_instances?.length || 0)) > 1 && ` x${(item.reported_defective_quantity && item.reported_defective_quantity > 0 ? 1 : 0) + (item.defect_instances?.length || 0)}`}
                                        </Badge>
                                      )}
                                      <Button 
                                        variant="warning" 
                                        size="sm" 
                                        onClick={() => addDefectInstance(item.id!)}
                                      >
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                        הוסף דיווח תקלה
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>

            {/* דיווחי תקלות קיימים */}
            <div className="mb-4">
              <h5>דיווחי תקלות קיימים:</h5>
              <Accordion defaultActiveKey="0" className="accordion-flush">
              {Object.entries(groupedDefectInstances).map(([category, subcategories], categoryIndex) => (
                <Accordion.Item eventKey={categoryIndex.toString()} key={category}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                      <span>
                        <strong>{category}</strong>
                        <Badge bg="secondary" className="ms-2">
                          {Object.values(subcategories).flat().length} דיווחי תקלות
                        </Badge>
                      </span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Accordion defaultActiveKey="0" className="accordion-flush">
                      {Object.entries(subcategories).map(([subcategory, instances], subcategoryIndex) => (
                        <Accordion.Item eventKey={`${categoryIndex}-${subcategoryIndex}`} key={`${category}-${subcategory}`}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span>
                                {subcategory}
                                <Badge bg="info" className="ms-2">
                                  {instances.length} דיווחי תקלות
                                </Badge>
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            {instances.map((instance) => (
                              <div key={instance.id} className="border rounded p-3 mb-3 bg-light">
                                <div className="row">
                                  {/* מידע על הפריט */}
                                  <div className="col-md-4">
                                    <h6 className="fw-bold">{instance.equipment_name}</h6>
                                    <p className="text-muted mb-1 small">
                                      {instance.category_name} - {instance.subcategory_name}
                                    </p>
                                  </div>

                                  {/* כמות תקולה */}
                                  <div className="col-md-3">
                                    <Form.Group>
                                      <Form.Label className="small fw-medium">כמות תקולה</Form.Label>
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        value={instance.quantity}
                                        onChange={(e) => updateDefectInstance(instance.equipment_id, instance.id, 'quantity', parseInt(e.target.value) || 0)}
                                        size="sm"
                                      />
                                    </Form.Group>
                                  </div>

                                  {/* פריוריטי */}
                                  <div className="col-md-3">
                                    <Form.Group>
                                      <Form.Label className="small fw-medium">פריוריטי</Form.Label>
                                      <Form.Select
                                        value={instance.priority}
                                        onChange={(e) => updateDefectInstance(instance.equipment_id, instance.id, 'priority', e.target.value)}
                                        size="sm"
                                      >
                                        {priorityOptions.map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Form.Group>
                                  </div>

                                  {/* כפתור מחיקה */}
                                  <div className="col-md-2 d-flex align-items-end">
                                    <Button 
                                      variant="outline-danger" 
                                      size="sm" 
                                      onClick={() => removeDefectInstance(instance.equipment_id, instance.id)}
                                      className="w-100"
                                    >
                                      ✕ מחק
                                    </Button>
                                  </div>
                                </div>

                                {/* תיאור התקלה */}
                                <div className="mt-3">
                                  <Form.Group>
                                    <Form.Label className="small fw-medium">תיאור התקלה</Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={2}
                                      value={instance.description}
                                      onChange={(e) => updateDefectInstance(instance.equipment_id, instance.id, 'description', e.target.value)}
                                      placeholder="תאר את התקלה..."
                                      size="sm"
                                    />
                                  </Form.Group>
                                </div>
                                <div className="mt-2">
                                  <Form.Group>
                                    <Form.Label className="small fw-medium">
                                      <FontAwesomeIcon icon={faImage} className="me-1 text-secondary" />
                                      תמונה (אופציונלי, עד 10MB)
                                    </Form.Label>
                                    <DefectImageInput
                                      disabled={submitting}
                                      selectedFileName={instance.imageFile?.name ?? null}
                                      onImageSelected={(f) =>
                                        updateDefectInstance(instance.equipment_id, instance.id, 'imageFile', f)
                                      }
                                      onClear={() =>
                                        updateDefectInstance(instance.equipment_id, instance.id, 'imageFile', null)
                                      }
                                      onValidationError={(msg) => {
                                        setMessageModalTitle('שים לב');
                                        setMessageModalContent(msg);
                                        setMessageModalVariant('warning');
                                        setCloseMainModalAfterMessage(false);
                                        setShowMessageModal(true);
                                      }}
                                    />
                                  </Form.Group>
                                </div>
                              </div>
                            ))}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
            </div>

            {equipmentList.length === 0 && !loading && (
              <Alert variant="warning" className="text-center">
                לא נמצא ציוד במחסן
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />
          ביטול
        </Button>
        <Button variant="warning" onClick={handleSubmit} disabled={submitting}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
          {submitting ? 'שולח...' : 'דווח על תקלות'}
        </Button>
      </Modal.Footer>
    </Modal>
    <ConfirmationModal
      show={showConfirmModal}
      onHide={() => setShowConfirmModal(false)}
      title={confirmModalTitle}
      message={confirmModalMessage}
      onConfirm={() => {
        if (confirmAction) confirmAction();
      }}
      confirmText="הוסף"
      cancelText="ביטול"
      confirmVariant="warning"
    />
    <MessageModal
      show={showMessageModal}
      onHide={handleMessageModalHide}
      title={messageModalTitle}
      message={messageModalContent}
      variant={messageModalVariant}
    />
    </>
  );
};

export default DefectReportGeneralModal;
